import { baseFields } from 'components/live/Toss/StatusTable'
import { IField } from 'components/shared/Table/table.interface'
import { ShippingType } from 'interfaces/broadcast.interface'
import { IDetailTodo, IOrderFromExcelByStatus } from 'interfaces/live.interface'
import {
    OrderStatusChangeKr,
    OrderStatusWithTotal,
    StatusTableProps,
} from 'interfaces/toss.interface'
import { numbericComma, phoneHypen } from './lib'
import { getOrdersFromExcelByStatus } from 'api/live.request'
import * as XLSX from 'xlsx'
import dayjs from 'dayjs'
import { STATUS_KOREAN } from 'config/const'
import { orderTableHeaders } from './tossOrder'

export function getShippingFee({
    shippingType,
    shippingPurchaseAmount,
    shippingCharge,
    totalProductPrice,
    remoteAreaShippingCharge,
    jejuShippingCharge,
    isTotal = true,
}: {
    shippingType: ShippingType
    shippingPurchaseAmount: number | null
    shippingCharge: number | null
    totalProductPrice: number
    remoteAreaShippingCharge: number
    jejuShippingCharge: number
    isTotal?: boolean
}): number {
    // props console
    // console.log(
    //     shippingType,
    //     shippingPurchaseAmount,
    //     shippingCharge,
    //     totalProductPrice,
    //     remoteAreaShippingCharge,
    //     jejuShippingCharge
    // )
    let price = 0
    switch (shippingType) {
        case 'CONDITIONAL_FREE_SHIPPING':
            if (typeof shippingPurchaseAmount === 'number' && typeof shippingCharge === 'number') {
                price = totalProductPrice >= shippingPurchaseAmount ? 0 : shippingCharge
            } else {
                price = 0
            }

            break
        case 'PAID_SHIPPING':
            price = shippingCharge ?? 0
            break
        case 'FREE_SHIPPING':
            price = 0
            break
        default:
            price = 0
            break
    }
    if (isTotal) {
        price = price + (remoteAreaShippingCharge ?? 0) + (jejuShippingCharge ?? 0)
    }
    return price
}

const getPromptText = (type: string) => {
    switch (type) {
        case 'memo2':
            return '메모를 입력해주세요..'
        case 'option':
            return '새로운 옵션을 입력해주세요.'
        case 'count':
            return '수량을 입력해주세요.'
        default:
            return '값을 입력해주세요.'
    }
}

export const tossPromptHandler = async ({
    broadcastId,
    guestId,
    orderId,
    type,
    data,
    defaultValue,
}: {
    broadcastId: string
    guestId: number
    orderId: number
    type: 'count'
    defaultValue?: string | number | null
    data?: IDetailTodo
}) => {
    if (!broadcastId) return

    const value = defaultValue ? String(defaultValue) : ''
    const promptText = getPromptText(type)

    let newValue = window.prompt(promptText, value)

    if (newValue === null) return
    if (newValue === '') newValue = null

    console.log('newValue', newValue)
}

export const statusColumnKeys: Record<OrderStatusWithTotal, string[]> = {
    REQUEST: [
        'checkbox',
        'nickname',
        'guestId',
        'productName',
        'productId',
        'option',
        'quantity',
        'price',
        'totalPrice',
        'shippingFee',
        'remoteFee',
        'expectedAmount',
        'receiverName',
        'receiverPhone',
        'fullAddress',
        'address1',
        'address2',
        'zipcode',
        'ordererPhone',
        'memo',
        'paymentDueDate',
        'sellerMemo',
    ],
    DEPOSIT_WAIT: [
        'checkbox',
        'orderId',
        'nickname',
        'guestId',
        'productName',
        'productId',
        'option',
        'quantity',
        'price',
        'totalPrice',
        'shippingFee',
        'remoteFee',
        'expectedAmount',
        'paymentMethod',
        'receiverName',
        'receiverPhone',
        'fullAddress',
        'address1',
        'address2',
        'zipcode',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
    COMPLETE: [
        'checkbox',
        'orderId',
        'nickname',
        'guestId',
        'paidAt',
        'productName',
        'productId',
        'option',
        'quantity',
        'price',
        'totalPrice',
        'shippingFee',
        'remoteFee',
        'finalPaidAmount',
        'paymentMethod',
        'receiverName',
        'receiverPhone',
        'fullAddress',
        'address1',
        'address2',
        'zipcode',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
    DELIVERY_READY: [
        'checkbox',
        'orderId',
        'nickname',
        'guestId',
        'deliveryReadyAt',
        'productName',
        'productId',
        'option',
        'quantity',
        'price',
        'totalPrice',
        'shippingFee',
        'remoteFee',
        'finalPaidAmount',
        'paymentMethod',
        'receiverName',
        'receiverPhone',
        'fullAddress',
        'address1',
        'address2',
        'zipcode',
        'trackingCompany',
        'trackingNumber',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
    DELIVERY: [
        'checkbox',
        'orderId',
        'nickname',
        'guestId',
        'deliveredAt',
        'productName',
        'productId',
        'option',
        'quantity',
        'price',
        'totalPrice',
        'shippingFee',
        'remoteFee',
        'finalPaidAmount',
        'paymentMethod',
        'receiverName',
        'receiverPhone',
        'fullAddress',
        'address1',
        'address2',
        'zipcode',
        'trackingCompany',
        'trackingNumber',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
    CANCEL: [
        'checkbox',
        'nickname',
        'guestId',
        'productName',
        'productId',
        'option',
        'quantity',
        'cancelledAt',
        'cancelReason',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
    REFUND_HOLD_CANCEL: [
        'checkbox',
        'orderId',
        'nickname',
        'guestId',
        'refundHoldAt',
        'cancelFailureReason',
        'refundProcess',
        'cancelReason',
        'cancelDetailReason',
        'productName',
        'productId',
        'option',
        'quantity',
        'refundAmount',
        'paymentMethod',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
    REFUND_HOLD_RETURN: [
        'checkbox',
        'orderId',
        'nickname',
        'guestId',
        'refundHoldAt',
        'cancelFailureReason',
        'refundProcess',
        'cancelReason',
        'cancelDetailReason',
        'productName',
        'productId',
        'option',
        'quantity',
        'refundAmount',
        'paymentMethod',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
    REFUND: [
        'orderId',
        'nickname',
        'guestId',
        'refundedAt',
        'refundMethod',
        'refundType',
        'cancelReason',
        'cancelDetailReason',
        'actualRefundAmount',
        'productName',
        'productId',
        'option',
        'quantity',
        'refundAmount',
        'shippingFee',
        'remoteFee',
        'additionalFee',
        'returnFee',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
    TOTAL: [
        'orderId',
        'nickname',
        'guestId',
        'orderedAt',
        'status',
        'statusUpdatedAt',
        'productName',
        'productId',
        'option',
        'quantity',
        'price',
        'totalPrice',
        'shippingFee',
        'remoteFee',
        'finalPaidAmount',
        'actualPaidAmount',
        'actualRefundAmount',
        'paymentMethod',
        'receiverName',
        'receiverPhone',
        'fullAddress',
        'address1',
        'address2',
        'zipcode',
        'trackingCompany',
        'trackingNumber',
        'ordererPhone',
        'memo',
        'sellerMemo',
    ],
}

export const getFieldsByStatus = (
    status: OrderStatusWithTotal,
    shippingInfo: StatusTableProps['shippingInfo']
): IField[] => {
    return statusColumnKeys[status].map((key) => {
        const field = { ...baseFields[key] }

        // if (key === 'shippingFee') {
        //     field.render = (_, __, original) => {
        //         const totalProductPrice = original?.order.totalPrice ?? 0
        //         const fee = getShippingFee({
        //             shippingType: shippingInfo.shippingType,
        //             shippingPurchaseAmount: shippingInfo.shippingPurchaseAmount,
        //             shippingCharge: shippingInfo.shippingCharge,
        //             totalProductPrice,
        //             remoteAreaShippingCharge: shippingInfo.remoteAreaShippingCharge ?? 0,
        //             jejuShippingCharge: shippingInfo.jejuShippingCharge ?? 0,
        //         })
        //         return numbericComma(fee)
        //     }
        // }

        return field as IField
    })
}

export const tossOrdersExportExcel = async (
    status: OrderStatusWithTotal,
    shippingInfo: {
        shippingCharge: number | null
        shippingPurchaseAmount: number | null
        shippingType: ShippingType
        jejuShippingCharge: number | null
        remoteAreaShippingCharge: number | null
    },
    broadcastId?: string,
    searchWord?: string,
    searchType?: string,
    broadcastDate?: string
) => {
    if (!broadcastId) return
    const Headers = statusColumnKeys[status]
    const statusKr = orderTableHeaders[status].label ?? ''
    const date = dayjs(broadcastDate).format('YY년 MM월 DD일')
    const fileName = `${date} ${statusKr} 목록.xlsx`
    // console.log('Headers', Headers)

    const res = await getOrdersFromExcelByStatus(broadcastId, status, searchWord, searchType)
    const data = res.data.data
    if (!data) return

    // console.log(data)

    // 데이터 파싱
    let parsedData = data.map((item: IOrderFromExcelByStatus) => {
        const itemStatus = item.status
        const parsedItem: any = {}

        Headers.forEach((header) => {
            switch (header) {
                case 'nickname':
                    parsedItem[header] = item.nickname
                    break
                case 'guestId':
                    parsedItem[header] = item.guestId
                    break
                case 'productName':
                    parsedItem[header] = item.productName
                    break
                case 'productId':
                    parsedItem[header] = item.number
                    break
                case 'option':
                    parsedItem[header] = item.productOption
                    break
                case 'quantity':
                    parsedItem[header] = item.count
                    break
                case 'price':
                    parsedItem[header] = item.price
                    break
                case 'totalPrice':
                    parsedItem[header] = item.totalPrice
                    break

                case 'receiverName':
                    parsedItem[header] = item.name
                    break
                case 'receiverPhone':
                    parsedItem[header] = phoneHypen(item.shippingPhone ?? '')
                    break
                case 'fullAddress':
                    parsedItem[header] = `${item.address ?? ''}`
                    break
                case 'address1':
                    parsedItem[header] = item.newAddress ?? item.address ?? ''
                    break
                case 'address2':
                    parsedItem[header] = item.newAddressDetail ?? ''
                    break
                case 'zipcode':
                    parsedItem[header] =
                        !item.zonecode || item.zonecode === '-' ? '' : item.zonecode
                    break
                case 'ordererPhone':
                    parsedItem[header] = phoneHypen(item.phone ?? '')
                    break
                case 'memo':
                    parsedItem[header] = item.memo
                    break
                case 'paymentDueDate':
                    parsedItem[header] = item.paymentDueDate
                        ? dayjs(item.paymentDueDate).format('YYYY-MM-DD HH:mm:ss')
                        : ' '
                    break
                case 'sellerMemo':
                    parsedItem[header] = item.memo2
                    break
                case 'orderId':
                    parsedItem[header] = item.orderCode
                    break
                case 'paymentMethod':
                    parsedItem[header] = item.paymentMethod
                    break
                case 'finalPaidAmount':
                    parsedItem[header] = item.balanceAmount
                    break
                case 'actualPaidAmount':
                    parsedItem[header] = item.totalAmount
                    break
                case 'actualRefundAmount':
                    parsedItem[header] =
                        Number(item.totalAmount ?? 0) - Number(item.balanceAmount ?? 0)
                    break
                case 'trackingCompany':
                    if (item.deliveryCompany && item.deliveryCompany !== '-') {
                        parsedItem[header] = item.deliveryCompany
                    } else {
                        parsedItem[header] = '-'
                    }
                    break
                case 'trackingNumber':
                    if (item.deliveryInvoice && item.deliveryInvoice !== '-') {
                        parsedItem[header] = item.deliveryInvoice
                    } else {
                        parsedItem[header] = '-'
                    }

                    break
                case 'refundAmount':
                    parsedItem[header] = Number(item.price ?? 0) * Number(item.count ?? 0)
                    // parsedItem[header] =
                    //     Number(item.totalAmount ?? 0) - Number(item.balanceAmount ?? 0)

                    break
                case 'cancelReason':
                    parsedItem[header] = item.cancelReason
                    break
                case 'cancelDetailReason':
                    parsedItem[header] = item.cancelReasonDetail
                    break
                case 'status':
                    parsedItem[header] =
                        OrderStatusChangeKr(item.status as OrderStatusWithTotal) ?? ''
                    break
                case 'remoteFee': // 제주 도서산간 추가 배송비
                    if (itemStatus === 'REFUND') {
                        parsedItem[header] = item.orderAdditionalShippingFee
                    } else {
                        parsedItem[header] =
                            Number(item.jejuShippingCharge ?? 0) +
                            Number(item.remoteAreaShippingCharge ?? 0)
                    }

                    break
                case 'shippingFee': // 배송비
                    if (itemStatus === 'REFUND') {
                        parsedItem[header] = item.orderShippingFee
                    } else {
                        parsedItem[header] = item.shippingFee
                    }
                    break
                case 'additionalFee': // 추가 배송비
                    if (itemStatus === 'REFUND') {
                        parsedItem[header] = item.orderRefundAddShippingFee
                    } else {
                        parsedItem[header] = item.refundAddShippingFee
                    }
                    break
                case 'returnFee': // 반품 배송비
                    if (itemStatus === 'REFUND') {
                        parsedItem[header] = item.orderRefundShippingFee
                    } else {
                        parsedItem[header] = item.refundShippingFee
                    }
                    break
                case 'expectedAmount':
                    if (status === 'REQUEST') {
                        // 신규 주문의 경우 추가 계산을 위해 데이터를 간소화
                        parsedItem[header] = Number(item.totalPayment ?? 0)
                    } else {
                        parsedItem[header] =
                            Number(item.totalPrice ?? 0) +
                            Number(item.shippingFee ?? 0) +
                            Number(item.jejuShippingCharge ?? 0) +
                            Number(item.remoteAreaShippingCharge ?? 0)
                    }
                    break
                case 'orderedAt':
                    parsedItem[header] = dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss')
                    break
                case 'paidAt':
                    if (item.paymentApprovedAt) {
                        parsedItem[header] = dayjs(item.paymentApprovedAt).format(
                            'YYYY-MM-DD HH:mm:ss'
                        )
                    } else if (item.statusUpdatedAt) {
                        parsedItem[header] = dayjs(item.statusUpdatedAt).format(
                            'YYYY-MM-DD HH:mm:ss'
                        )
                    } else {
                        parsedItem[header] = dayjs(item.updatedAt).format('YYYY-MM-DD HH:mm:ss')
                    }
                    break
                case 'statusUpdatedAt':
                case 'deliveryReadyAt':
                case 'deliveredAt':
                case 'cancelledAt':
                case 'refundedAt':
                case 'refundHoldAt':
                    if (item.statusUpdatedAt) {
                        parsedItem[header] = dayjs(item.statusUpdatedAt).format(
                            'YYYY-MM-DD HH:mm:ss'
                        )
                    } else {
                        parsedItem[header] = dayjs(item.updatedAt).format('YYYY-MM-DD HH:mm:ss')
                    }
                    break
                default:
                    if (header === 'checkbox') return
                    // @ts-ignore
                    parsedItem[header] = item[header] ?? '' // 기본값 설정ㄴ
                    // 알 수 없는 필드 처리
                    break
            }

            parsedItem[`id`] = item.id
        })
        // console.log(item, parsedItem)
        return parsedItem
    })
    // 배송비 계산
    // console.log(parsedData)
    // parsedData 에서 orderCode 가 같은 케이스 분리
    const groupedData: Record<string, any[]> = {}
    parsedData.forEach((item) => {
        const orderCode = item.orderId ?? item.nickname
        if (!groupedData[orderCode]) {
            groupedData[orderCode] = []
        }
        groupedData[orderCode].push(item)
    })
    // 그룹화된 키를 기준으로 반복문 돌기
    Object.keys(groupedData).forEach((key) => {
        const items = groupedData[key]
        let totalPrice = 0
        let remoteFee = 0

        const idx: number[] = []

        items.map((e) => {
            // console.log(e)
            remoteFee = e.remoteFee
            const price = e.quantity * e.price
            if (e.status === '신규주문' || status === 'REQUEST') {
                totalPrice += price
                idx.push(e.id)
            }
        })
        // 신규 배송에서는 배송비를 클라이언트에서 계산
        if (idx.length !== 0) {
            const shippingFee = getShippingFee({
                shippingType: shippingInfo.shippingType,
                shippingPurchaseAmount: shippingInfo.shippingPurchaseAmount,
                shippingCharge: shippingInfo.shippingCharge,
                totalProductPrice: totalPrice,
                remoteAreaShippingCharge: shippingInfo.remoteAreaShippingCharge ?? 0,
                jejuShippingCharge: shippingInfo.jejuShippingCharge ?? 0,
                isTotal: false,
            })

            parsedData = parsedData.map((item) => {
                if (idx.includes(item.id)) {
                    // console.log(totalPrice, item.expectedAmount, shippingFee, remoteFee)
                    return {
                        ...item,
                        shippingFee: shippingFee,
                        expectedAmount: Number(item.expectedAmount ?? 0) + shippingFee + remoteFee,
                    }
                }
                return item
            })
        }
    })
    // console.log(groupedData)

    const sheetName = '주문내역'

    if (parsedData.length === 0) return null

    let removedData = parsedData.map((e) => {
        const { id, ...rest } = e
        return rest
    })

    let getHeadersByData = Object.keys(removedData[0])
    // console.log('getHeadersByData', getHeadersByData)
    if (status === 'TOTAL' && getHeadersByData.includes('expectedAmount')) {
        getHeadersByData = getHeadersByData.filter((header) => header !== 'expectedAmount')
    }
    // 한글 치환
    const headers = getHeadersByData.map((header) => {
        switch (header) {
            case 'nickname':
                return '주문자 닉네임'
            case 'guestId':
                return '유저 식별번호'
            case 'productName':
                return '상품명'
            case 'productId':
                return '상품번호'
            case 'option':
                return '옵션'
            case 'quantity':
                return '수량'
            case 'price':
                return '판매가'
            case 'totalPrice':
                return '총상품금액'
            case 'shippingFee':
                return '배송비'
            case 'remoteFee':
                return '제주/도서산간 배송비'
            case 'expectedAmount':
                return '결제예정금액'
            case 'receiverName':
                return '수령인 이름'
            case 'receiverPhone':
                return '수령인연락처'
            case 'fullAddress':
                return '전체주소'
            case 'address1':
                return '기본주소'
            case 'address2':
                return '상세주소'
            case 'zipcode':
                return '우편번호'
            case 'ordererPhone':
                return '주문자연락처'
            case 'memo':
                return '요청사항'
            case 'paymentDueDate':
                return '결제연장기한'
            case 'sellerMemo':
                return '판매자 메모'
            case 'orderId':
                return '주문번호'
            case 'paymentMethod':
                return '결제수단'
            case 'paidAt':
                return '결제완료일시'
            case 'finalPaidAmount':
                return '최종결제금액'
            case 'deliveryReadyAt':
                return '배송준비일시'
            case 'trackingCompany':
                return '택배사'
            case 'trackingNumber':
                return '송장번호'
            case 'deliveredAt':
                return '배송완료일시'
            case 'cancelledAt':
                return '주문취소일시'
            case 'cancelReason':
                return status === 'REFUND' ? '환불사유' : '취소사유'
            case 'cancelDetailReason':
                return status === 'REFUND' ? '환불상세사유' : '취소상세사유'
            case 'refundedAt':
                return '취소/반품완료일시'
            case 'actualRefundAmount':
                return '실환불금액'
            case 'refundAmount':
                return '상품금액'
            case 'refundMethod':
                return '환불방식'
            case 'refundType':
                return '환불유형'
            case 'additionalFee':
                return '추가배송비'
            case 'orderedAt':
                return '주문일시'
            case 'status':
                return '주문상태'
            case 'statusUpdatedAt':
                return '상태변경일시'
            case 'actualPaidAmount':
                return '실결제금액'
            case 'returnFee':
                return '반품배송비'
            default:
                return ''
        }
    })

    // console.log(headers)

    const rows: (string | number)[][] = [headers]

    removedData.forEach((item) => {
        const row: (string | number)[] = []
        getHeadersByData.forEach((header) => {
            row.push(item[header])
        })
        rows.push(row)
    })

    const sheet = XLSX.utils.aoa_to_sheet(rows)
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, sheet, sheetName)
    XLSX.writeFile(book, fileName)
    // console.log('parsedData', parsedData)
}
